import { makeid } from 'utils/helpers/makeid';

import {
  booleanOptions,
  notEscalatedOptions,
  nursingInterventionOptions,
  stringBooleanOptions,
  commonFactors,
} from './common';

import type { TFormItem, IFormItemOption } from '../types';

export const hypotensionSymptoms: IFormItemOption<string>[] = [
  {
    label: 'Shortness of breath (without exertion)',
    value: 'shortnessBreathWithoutExertion',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Chest Pain/Tightness',
    value: 'chestPainTightness',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Dizziness/Lightheadedness or confusion',
    value: 'dizziness',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Shallow/rapid breathing',
    value: 'rapidBreathing',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Blue tinge to lips or fingers',
    value: 'difficultySpeaking',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Feeling cold, tired, lethargic',
    value: 'lethargic',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Wet productive cough',
    value: 'wetCough',
    key: makeid(10),
  },
  {
    label: 'Yellow, green, tan or bloody sputum',
    value: 'sputum',
    key: makeid(10),
  },
  {
    label: 'Fever/chills',
    value: 'feverChills',
    key: makeid(10),
  },
  {
    label: 'Dry hacking cough',
    value: 'cough',
    key: makeid(10),
  },
  {
    label: 'Shortness of breath (with exertion)',
    value: 'shortnessBreathWithExertion',
    key: makeid(10),
  },
  {
    label: 'Pursed lip breathing/tripod breathing',
    value: 'tripodBreathing',
    key: makeid(10),
  },
];

export const factorsAssessment: IFormItemOption<string>[] = [
  ...commonFactors,
  {
    label: 'Taking a few deep breaths before reading',
    value: 'deepBreaths',
    key: makeid(10),
  },
  {
    label: 'Hands are warm and dry',
    value: 'warmAndDry',
    key: makeid(10),
  },
  {
    label: 'Wearing fingernail polish',
    value: 'fingernailPolish',
    key: makeid(10),
  },
];

export const assessmentSpo2Form: TFormItem[] = [
  {
    key: makeid(10),
    title: '#1 Did you speak to patient?',
    description:
      'Choose other if unlisted family member & type in their relationship. (Ex: Daughter in law) Add Name of Individual you spoke to in other.',
    type: 'radio',
    required: true,
    name: 'patient',
    options: [
      { value: 'Patient', label: 'Patient', key: makeid(10) },
      { value: 'Spouse', label: 'Spouse', key: makeid(10) },
      { value: 'Caregiver', label: 'Caregiver', key: makeid(10) },
      {
        value: 'Emergency Contact',
        label: 'Emergency Contact',
        key: makeid(10),
      },
      { value: null, label: 'Other', key: makeid(10) },
    ],
    extra: [
      {
        type: 'doubleRowInput',
        name: 'other',
        key: makeid(10),
        options: [
          { label: 'Other Member', value: 'other', key: makeid(10) },
          { label: 'Relationship', value: 'relationship', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#2 Mental Status Assessment',
    description:
      'Oriented and Coherent 1: Person 2: Person, place 3: Person, place, time 4: Person, place, time, situation?',
    type: 'radio',
    required: false,
    name: 'AOC',
    options: [
      { value: 'x1', label: 'x1', key: makeid(10) },
      { value: 'x2', label: 'x2', key: makeid(10) },
      { value: 'x3', label: 'x3', key: makeid(10) },
      { value: 'x4', label: 'x4', key: makeid(10) },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#3 Appointments',
    type: 'datepicker',
    name: 'datepicker',
    required: false,
    options: [
      {
        value: 'last',
        label: "Last Doctor's Appointment",
        type: 'max',
        key: makeid(10),
      },
      {
        value: 'next',
        label: "Next Doctor's Appointment",
        type: 'min',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#4 Patient is able to take another reading at time of call?',
    subtitle: 'Is the reading still out of bounds?',
    type: 'radio',
    required: true,
    name: 'isOneMoreReading',
    options: stringBooleanOptions,
    extra: [
      {
        type: 'radio',
        name: 'bounds',
        key: makeid(10),
        label: 'Is the reading still out of bounds?',
        options: booleanOptions,
        divider: true,
      },
      {
        type: 'input',
        name: 'repeatReadingWas',
        key: makeid(10),
        options: [
          {
            label: 'The repeat reading was:',
            value: 'repeatReadingWas',
            key: makeid(10),
          },
        ],
      },
    ],
    readingTypeFor: ['min'],
  },
  {
    key: makeid(10),
    title: '#5	Signs and Symptoms of Hypoxia',
    type: 'radio-list',
    name: 'symptoms',
    required: true,
    options: hypotensionSymptoms,
    extra: [
      {
        name: 'symptomsOnset',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min'],
  },
  {
    key: makeid(10),
    title: '#6 Did patient take routine medications as prescribed?',
    type: 'radio',
    required: false,
    name: 'isTakeMedications',
    options: stringBooleanOptions,
    extra: [
      {
        type: 'checkbox',
        name: 'prescribed',
        key: makeid(10),
        options: [
          { value: 'oxygen', label: 'Oxygen', key: makeid(10) },
          { value: 'nebulizer', label: 'Nebulizer', key: makeid(10) },
          { value: 'rescueInhaler', label: 'Rescue inhaler', key: makeid(10) },
          { value: 'oralSteroid', label: 'Oral steroid', key: makeid(10) },
        ],
      },
      {
        type: 'input',
        name: 'otherPrescribed',
        key: makeid(10),
        options: [
          { label: 'Other', value: 'otherPrescribed', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min'],
  },
  {
    key: makeid(10),
    title: '#7 Did patient take any prescribed PRN medications?',
    type: 'radio',
    required: false,
    name: 'isTakePrnMedications',
    options: stringBooleanOptions,
    extra: [
      {
        type: 'checkbox',
        name: 'prnMedications',
        key: makeid(10),
        options: [
          { value: 'oxygen', label: 'Oxygen', key: makeid(10) },
          { value: 'nebulizer', label: 'Nebulizer', key: makeid(10) },
          { value: 'rescueInhaler', label: 'Rescue inhaler', key: makeid(10) },
          { value: 'oralSteroid', label: 'Oral steroid', key: makeid(10) },
        ],
      },
      {
        type: 'input',
        name: 'otherPrnMedications',
        key: makeid(10),
        options: [
          { label: 'Other', value: 'otherPrnMedications', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min'],
  },
  {
    key: makeid(10),
    title: '#8 Does patient wear home O2?',
    description: 'What is the current setting?',
    name: 'wearO2Home',
    required: false,
    type: 'radio',
    options: booleanOptions,
    readingTypeFor: ['min'],
    flexBlock: true,
    extra: [
      {
        type: 'inputNumber',
        name: 'homeO2Setting',
        key: makeid(10),
        options: [
          {
            extraType: 'inputNumber',
            label: 'Setting [L/min]:',
            value: 'homeO2Setting',
            width: 100,
            key: makeid(10),
          },
          {
            extraType: 'inputNumberRadio',
            value: 'homeO2SettingValue',
            label: '',
            key: makeid(10),
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: '#9 Factors Assessment',
    type: 'radio-list',
    required: false,
    options: factorsAssessment,
    name: 'factorsAssessment',
    readingTypeFor: ['min'],
  },
  {
    key: makeid(10),
    title: '#10 Nursing Intervention',
    type: 'checkbox',
    name: 'nursingIntervention',
    required: true,
    options: nursingInterventionOptions,
    extra: [
      {
        type: 'labelCheckbox',
        name: 'notEscalated',
        key: makeid(10),
        label: 'Not Escalated',
        options: notEscalatedOptions,
        divider: true,
      },
      {
        name: 'interventionDetails',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min'],
  },
  {
    key: makeid(10),
    title: '#11 Patient Understanding',
    description:
      'Patient verbalized understanding and is agreeable to nursing recommendation?',
    type: 'radio',
    required: false,
    name: 'verbalized',
    options: booleanOptions,
    readingTypeFor: ['min'],
  },
];
